import React, { useEffect, useState } from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  parseEntityRef,
} from '@backstage/catalog-model';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';

/**
 * Custom scaffolder field that shows only the groups the signed-in user
 * belongs to, instead of all groups in the catalog.
 *
 * It reads group membership from the user's Backstage identity token
 * (the `ownershipEntityRefs` populated by `signInWithCatalogUser`),
 * then fetches display names from the catalog.
 *
 * Usage in template.yaml:
 * ```yaml
 * owner:
 *   title: Owner
 *   type: string
 *   description: Owner of the component
 *   ui:field: MyGroupsPicker
 * ```
 */
export const MyGroupsPicker = (
  props: FieldExtensionComponentProps<string>,
) => {
  const { onChange, rawErrors, required, formData, uiSchema, schema } = props;
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);

  const [groups, setGroups] = useState<{ ref: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        const groupRefs = identity.ownershipEntityRefs.filter(ref => {
          try {
            const parsed = parseEntityRef(ref);
            return parsed.kind.toLowerCase() === 'group';
          } catch {
            return false;
          }
        });

        // Fetch display names from catalog
        const results = await Promise.all(
          groupRefs.map(async ref => {
            try {
              const parsed = parseEntityRef(ref);
              const entity = await catalogApi.getEntityByRef(ref);
              const displayName =
                entity?.metadata?.title ??
                entity?.metadata?.name ??
                parsed.name;
              return { ref: `group:default/${parsed.name}`, name: displayName };
            } catch {
              const parsed = parseEntityRef(ref);
              return { ref: `group:default/${parsed.name}`, name: parsed.name };
            }
          }),
        );

        setGroups(results);

        // Auto-select if there's only one group
        if (results.length === 1 && !formData) {
          onChange(results[0].ref);
        }
      } catch {
        setGroups([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [identityApi, catalogApi, onChange, formData]);

  const label = (schema?.title as string) ?? 'Owner';
  const description = (schema?.description as string) ?? uiSchema?.['ui:description'] ?? '';
  const hasError = rawErrors && rawErrors.length > 0;

  if (loading) {
    return (
      <FormControl fullWidth margin="normal">
        <InputLabel>{label}</InputLabel>
        <Select disabled value="">
          <MenuItem value="">Loading your groups...</MenuItem>
        </Select>
        {description && <FormHelperText>{description}</FormHelperText>}
      </FormControl>
    );
  }

  if (groups.length === 0) {
    return (
      <FormControl fullWidth margin="normal" error>
        <InputLabel>{label}</InputLabel>
        <Select disabled value="">
          <MenuItem value="">No groups found</MenuItem>
        </Select>
        <FormHelperText>
          You are not a member of any groups. Contact your administrator to be
          added to an Entra ID group.
        </FormHelperText>
      </FormControl>
    );
  }

  return (
    <FormControl
      fullWidth
      margin="normal"
      required={required}
      error={hasError}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        value={formData ?? ''}
        onChange={e => onChange(e.target.value as string)}
      >
        {groups.map(g => (
          <MenuItem key={g.ref} value={g.ref}>
            {g.name}
          </MenuItem>
        ))}
      </Select>
      {description && <FormHelperText>{description}</FormHelperText>}
    </FormControl>
  );
};
